// src/playback-runtime/lifecycle/useAutoResume.ts
"use client";

/*
 Auto resume audio after:
 - network drop
 - tab restore
 - HLS stall
*/

import { useEffect, useRef } from "react";

type Opts = {
  src: string;
};

export function useAutoResume(
  audioRef: React.RefObject<HTMLAudioElement | null>,
  { src }: Opts
) {
  const lastTimeRef = useRef(0);
  const shouldResumeRef = useRef(false);
  const recoveringRef = useRef(false);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;

    /* ---------- remember playback position ---------- */
    const remember = () => {
      lastTimeRef.current = el.currentTime || 0;
    };

    el.addEventListener("timeupdate", remember);

    /* ---------- detect stall ---------- */
    const onStall = () => {
      if (!el.paused) {
        shouldResumeRef.current = true;
      }
    };

    el.addEventListener("waiting", onStall);
    el.addEventListener("stalled", onStall);
    el.addEventListener("suspend", onStall);
    el.addEventListener("error", onStall);

    /* ---------- recover ---------- */
    const tryRecover = async () => {
      if (!shouldResumeRef.current) return;
      if (recoveringRef.current) return;
      if (!navigator.onLine) return;

      recoveringRef.current = true;

      try {
        const t = lastTimeRef.current || 0;

        // reload source
        el.src = src;
        el.load();

        // wait metadata
        await new Promise<void>((resolve) => {
          const fn = () => {
            el.removeEventListener("loadedmetadata", fn);
            resolve();
          };
          el.addEventListener("loadedmetadata", fn);
        });

        // restore position
        if (t > 0 && isFinite(el.duration)) {
          el.currentTime = Math.min(t, el.duration - 0.2);
        }

        await el.play();

        shouldResumeRef.current = false;
      } catch {
        // will retry next reconnect
      } finally {
        recoveringRef.current = false;
      }
    };

    /* ---------- online ---------- */
    window.addEventListener("online", tryRecover);
    document.addEventListener("visibilitychange", tryRecover);

    return () => {
      el.removeEventListener("timeupdate", remember);
      el.removeEventListener("waiting", onStall);
      el.removeEventListener("stalled", onStall);
      el.removeEventListener("suspend", onStall);
      el.removeEventListener("error", onStall);

      window.removeEventListener("online", tryRecover);
      document.removeEventListener("visibilitychange", tryRecover);
    };
  }, [audioRef, src]);
}
